import type { HelmFlowEdge, HelmFlowNode } from './helmFlow.graph';
import type { SavedLayout } from './helmFlowSavedLayout';

// The contact-journey graph (/superadmin/flow): auto-loader intake → research →
// dossier → qualify → sequence, with per-step error handling and parallel constructs.
// TOPOLOGY (node ids + edges) is kept in lockstep with the engine's executable spec
// (`FLOW_JOURNEY` in @poseidon/types) by `flow2.parity.vitest.ts` — the display copy
// (title/desc/emits/layout) lives here; the walkable structure is the single source of truth.
export const FLOW2_NODES: HelmFlowNode[] = [
  // ── Auto-loader intake lane (upstream of the per-contact pipeline): the hourly
  //    cron pulls contacts from Sales Nav, stages them as pending candidates, then
  //    each candidate passes the two cheap pre-spend gates (recontact + title screen)
  //    BEFORE any Unipile profile fetch. Grounded in worker.service.ts:5628-5715. ──
  {
    id: 'loader.cron',
    kind: 'guard',
    cron: true,
    title: 'Auto-load contacts',
    subtitle: 'cron · hourly',
    desc: 'The auto-loader heartbeat (cron.autoLoadSweep, ~hourly; or a manual “Run now”). For each opted-in campaign it computes runway = (queued sends + ⅓ · in-flight) / daily limit. When runway < 3 days AND the campaign is eligible (auto_load_enabled + team flag + a connected LinkedIn API account + an enabled, non-exhausted search + not paused/saturated), it kicks off a run to pull fresh contacts.',
    emits: 'runway < 3 days → Pull contacts',
  },
  {
    id: 'loader.pull',
    kind: 'effect',
    title: 'Pull contacts from Sales Navigator',
    subtitle: 'LinkedIn API search · ≤200 / run',
    desc: 'Runs each enabled search and imports a page of LinkedIn Sales Navigator results via the LinkedIn API — up to 200 contacts per search per run, resuming from the last cursor. These are lightweight SEARCH-RESULT fields (name, title, headline, company, location) — NOT the full profile (that per-contact fetch happens later). Skips a page when ≥90% are already candidates; marks a search exhausted when it runs off the end.',
    emits: 'imported contacts → Stage as pending candidate',
  },
  {
    id: 'loader.stage',
    kind: 'effect',
    pill: true,
    title: 'Auto-loading',
    subtitle: 'Contact status · staged (pending)',
    desc: 'Intake stages each imported person as a campaign candidate at status = `pending` (pgCandidateIntakeBulk) — surfaced to the user as “auto-loading”. The ONE writer of `pending` (pending ⟺ last_attempted_at IS NULL) and the sole bridge into the per-contact pipeline (every staged row is then claimed by the candidate worker). Stamps the source search for provenance. Revives a prior non-rejected terminal back to pending; a previously rejected contact is a permanent skip.',
    emits: '',
  },
  {
    id: 'recontact.gate',
    kind: 'filter',
    title: 'Recontact gate',
    subtitle: 'Anti-repeat · runs first, free',
    desc: 'The FIRST gate on a claimed candidate, before ANY spend (it runs even before the title screen). Deterministic date math — free. Two checks: (1) team-wide — did the contact reply to ANY teammate’s campaign? blocked until `auto_load_engaged_recontact_after_days` after the reply (unset = never re-added) → `contact_engaged`. (2) your history — did you already run a campaign that sent outreach to them? blocked until `auto_load_recontact_after_days` after it (unset = never) → `recently_contacted`. Only applies to auto-loaded candidates. A block sets status = rejected but deliberately does NOT stamp `rejected_at`, so a later import re-evaluates against the then-current windows.',
    emits: 'not contacted before → Title screen · replied (team) → skip · already contacted (you) → skip',
  },
  {
    id: 'title.screen',
    kind: 'filter',
    title: 'Title screen',
    subtitle: 'Cheap ICP pre-screen · AI',
    desc: 'A cheap pre-spend gate (POS-2367): Claude judges ONLY the fields we already hold from the search import — name, title, headline, company — against the campaign’s audience / avoid, and rejects the obviously-wrong BEFORE any LinkedIn API profile fetch or Tavily dossier spend. Fails OPEN on a model error (proceeds — the real gate is Qualify after the dossier). Skipped when the campaign has no audience, or for human-approved rows. Reject → `title_screen_disqualified`.',
    emits: 'fits ICP → research · off-ICP → disqualified',
    ai: {
      modelKey: 'candidate_title_screen',
      input:
        'The contact’s name / title / headline / company (from the Sales Nav import) + the campaign audience / avoid / criteria → verdict pass / reject, with a reason.',
    },
  },
  {
    id: 'status.replied_teammate',
    kind: 'dead',
    pill: true,
    title: 'Replied to teammate',
    subtitle: 'Contact status · recontact skip',
    desc: 'The recontact gate blocked this contact: they replied to a teammate’s campaign within the engaged window (`auto_load_engaged_recontact_after_days`). Sets status = rejected (failure_type `contact_engaged`). NOT permanent — rejected_at is left NULL, so a later import re-evaluates against the then-current window.',
    emits: '',
  },
  {
    id: 'status.already_contacted',
    kind: 'dead',
    pill: true,
    title: 'Already contacted',
    subtitle: 'Contact status · recontact skip',
    desc: 'The recontact gate blocked this contact: you already ran a campaign that sent outreach to them within the recontact window (`auto_load_recontact_after_days`). Sets status = rejected (failure_type `recently_contacted`). NOT permanent — rejected_at is left NULL, so a later import re-evaluates.',
    emits: '',
  },
  {
    id: 'status.title_disqualified',
    kind: 'dead',
    pill: true,
    title: 'Title disqualified',
    subtitle: 'Contact status · terminal',
    desc: 'The title screen rejected this contact: Claude judged the title / headline / company an obvious ICP miss before any LinkedIn API or Tavily spend. Sets status via pgCandidateMarkDisqualified (failure_type `title_screen_disqualified`).',
    emits: '',
  },
  {
    id: 'seq.add',
    kind: 'entry',
    title: 'Add contact to sequence',
    subtitle: 'Sequence entry',
    desc: 'A contact enters the sequence here.',
    emits: 'enroll → dedupe check',
  },
  {
    id: 'seq.dupe_check',
    kind: 'filter',
    title: 'Already in outreach?',
    subtitle: 'Anti-double-add · runs first, free',
    desc: 'Before a manually-added contact is marked “adding”, checks whether they’re already being worked elsewhere: an in-flight enrollment in ANOTHER flow2 sequence, an active campaign/cadence (blocking states — the same “in-progress campaign” set the add-to-campaign paths block on), or a staged switch candidate. First hit wins; the contact is skipped (never double-messaged) and the trace names the sequence/campaign/switch. Fails OPEN on a lookup error (allows the add). Re-adding to the SAME sequence never reaches here — the enrollment upsert reuses the existing row.',
    emits: 'not already in outreach → Adding · already active → skip',
  },
  {
    id: 'status.already_active',
    kind: 'dead',
    pill: true,
    title: 'Already in outreach',
    subtitle: 'Sequence entry · skip',
    desc: 'The dedupe gate blocked this contact: they’re already active in another sequence, an active campaign/cadence, or a staged switch, so they were NOT added (to avoid double-messaging). The enrollment ends here; the event records which one (e.g. “already in sequence …”).',
    emits: '',
  },
  {
    id: 'status.adding',
    kind: 'effect',
    pill: true,
    title: 'Adding',
    subtitle: 'Contact status (user + team)',
    desc: 'Sets the contact’s status for this user + team to “adding”, then continues.',
    emits: '',
  },
  {
    id: 'research.should',
    kind: 'guard',
    title: 'Should query LinkedIn API',
    subtitle: 'Recency gate',
    desc: 'Recency gate on the contact property `contact.last_enriched_at` (when we last queried the LinkedIn API for this contact): unset → never queried; older than 1 day → stale; within the last day → fresh, so the LinkedIn API query is skipped.',
    emits: 'never · stale · fresh',
  },
  {
    id: 'unipile.enabled',
    kind: 'guard',
    title: 'Is LinkedIn API enabled',
    subtitle: 'Flag + connection check',
    desc: 'Gate before any LinkedIn API call: is the LinkedIn API usable for this team? Checks the LinkedIn API flag is enabled and a LinkedIn account is connected. Both pass → Success; no connected account → Not connected.',
    emits: 'success · not connected',
  },
  {
    id: 'unipile.slot',
    kind: 'guard',
    title: 'Acquire LinkedIn API slot',
    subtitle: 'LinkedIn API · 5 slots',
    desc: 'Acquires 1 of 5 concurrency slots per (user, team) (60s TTL) before the fetch. The cap is SHARED with cadence sends, so research and outreach mutually throttle the account. If all 5 are taken, the contact does NOT fail — it waits and retries on the next sweep (~60s) until a slot frees.',
    emits: 'slot acquired · slot full',
  },
  {
    id: 'status.researching',
    kind: 'effect',
    pill: true,
    title: 'Researching',
    subtitle: 'Contact status (user + team)',
    desc: 'Sets the contact’s status for this user + team to “researching”, then runs the LinkedIn API fetch.',
    emits: '',
  },
  {
    id: 'status.waiting_slot',
    kind: 'blocked',
    pill: true,
    title: 'Waiting for LinkedIn API slot',
    subtitle: 'Contact status (user + team)',
    desc: 'Sets the contact’s status for this user + team to “waiting for LinkedIn API slot”.\n\nAll 5 LinkedIn API slots are busy, so the contact parks here — a terminal state, it is not looping.\n\nThe “Resume waiting contacts” cron re-enqueues it later to re-attempt Acquire LinkedIn API slot.',
    emits: '',
  },
  {
    id: 'cron.sweep',
    kind: 'guard',
    cron: true,
    title: 'Resume waiting contacts',
    subtitle: 'cron · every few min',
    desc: 'Scheduled cron, every few minutes.\n\nRe-runs any contact whose wait timer (next_run_at) has elapsed — pulling it out of “Waiting for LinkedIn API slot” and back into Acquire LinkedIn API slot.\n\nSlot-blind: it wakes every waiting contact at once; they race for slots, and losers go back to waiting.\n\nDB: flow.enrollment_arm · next_run_at ≤ now · FOR UPDATE SKIP LOCKED · limit 200.',
    emits: 're-enqueue waiting contacts → Acquire LinkedIn API slot',
  },
  {
    id: 'unipile.fetch',
    kind: 'effect',
    title: 'Fetch LinkedIn profile',
    subtitle: 'LinkedIn API',
    desc: 'Queries the LinkedIn API for the contact’s LinkedIn profile, trying each stored identifier in turn — while holding the LinkedIn API slot. The instant the query returns (success or error), it hands off to Release LinkedIn API slot.',
    emits: 'query returns → Release LinkedIn API slot',
  },
  {
    id: 'unipile.slot.release',
    kind: 'effect',
    title: 'Release LinkedIn API slot',
    subtitle: 'Frees the held slot · then routes',
    desc: 'Frees the LinkedIn API slot the moment the fetch returns — on EVERY outcome — so it’s never held while a contact parks or errors (freeing it for other contacts + cadence sends).\n\nThen routes on the query’s result: success → web search; the rest → the matching status / prompt / alert.',
    emits: 'success · no-profile · review · disconnected · rate-limit · no-salesnav · misconfigured · gave-up',
  },
  {
    id: 'title.rescreen',
    kind: 'filter',
    title: 'Title re-screen',
    subtitle: 'Cheap post-fetch ICP gate · AI',
    desc: 'A second cheap gate AFTER the LinkedIn API profile fetch, BEFORE any Tavily web-search + dossier spend: Claude re-runs the title screen (same `candidate_title_screen` prompt) on the FRESHLY-FETCHED name / title / headline / company — catching contacts the sparse pre-fetch import missed, or whose enriched profile now reads off-ICP. Also gates the “fresh (< 1 day)” path that skips the fetch. Fails OPEN on a model error (proceeds — the real gate is Qualify after the dossier). Skipped when the sequence has no audience. Reject → `title_screen_disqualified`.',
    emits: 'fits ICP → web search · off-ICP → disqualified',
    ai: {
      modelKey: 'candidate_title_screen',
      input:
        'The contact’s freshly-fetched name / title / headline / company + the sequence audience / avoid → verdict pass / reject, with a reason.',
    },
  },
  {
    id: 'cron.sweep_tavily',
    kind: 'guard',
    cron: true,
    title: 'Resume waiting contacts (tavily)',
    subtitle: 'cron · every few min',
    desc: 'The Tavily-stage counterpart of “Resume waiting contacts”: finds contacts parked at “Waiting for tavily slot” whose wait timer (next_run_at) has elapsed and re-enqueues them to re-attempt Acquire Tavily slot.',
    emits: 're-enqueue waiting contacts → Acquire Tavily slot',
  },
  {
    id: 'tavily.slot',
    kind: 'guard',
    title: 'Acquire Tavily slot',
    subtitle: 'Redis semaphore · 5 slots',
    desc: 'Acquires 1 of 5 Redis semaphore slots per team (30s TTL) before the web search — bounds concurrent Tavily calls per team. If all 5 are taken, the contact does NOT fail: it frees any held LinkedIn API slot and waits; the “Resume waiting contacts” cron retries.',
    emits: 'slot acquired · slot full',
  },
  {
    id: 'status.waiting_tavily',
    kind: 'blocked',
    pill: true,
    title: 'Waiting for tavily slot',
    subtitle: 'Contact status (user + team)',
    desc: 'All 5 Tavily slots are busy. Sets the contact’s status to “waiting for tavily slot” and parks — a terminal state, not looping. The “Resume waiting contacts” cron re-enqueues it to re-attempt Acquire Tavily slot.',
    emits: '',
  },
  {
    id: 'tavily.search',
    kind: 'effect',
    title: 'Tavily search the web',
    subtitle: 'Web enrichment',
    desc: 'Searches the web via Tavily to enrich the contact’s dossier (company, role, recent signals) before qualification — while holding the Tavily slot. When the search returns, it hands off to Release Tavily slot.',
    emits: 'search returns → Release Tavily slot',
  },
  {
    id: 'tavily.slot.release',
    kind: 'effect',
    title: 'Release Tavily slot',
    subtitle: 'Frees the held slot · then routes',
    desc: 'Frees the Tavily slot the moment the search returns — on EVERY outcome — so it’s never held while a contact parks or errors. Then routes on the result: results / nothing found → onward · rate-limited → back off · API error → give up.',
    emits: 'success · no results · rate-limited · failed',
  },
  {
    id: 'unipile.disconnected',
    kind: 'blocked',
    pill: true,
    title: 'Linkedin disconnected',
    subtitle: 'Contact status (user + team)',
    desc: 'Sets the contact’s status for this user + team to “linkedin disconnected” — the LinkedIn account isn’t connected.',
    emits: '',
  },
  {
    id: 'contact.delete',
    kind: 'effect',
    title: 'No LinkedIn profile',
    subtitle: 'Flagged · route to delete',
    desc: 'No LinkedIn profile could be resolved (no identifier, or the profile is deleted / private / not found). The contact is useless for LinkedIn outreach, so it is flagged for deletion — this marker routes onward to the Delete contact executor rather than leaving a dead row parked here.',
    emits: 'no profile → Delete contact',
  },
  {
    id: 'contact.purge',
    kind: 'effect',
    title: 'Delete contact',
    subtitle: 'Destructive · hard-delete',
    desc: 'Hard-deletes the contact and every row that references it (via pgContactDeleteFully); the contact’s flow enrollment rows cascade away too, so the person drops off this graph entirely. GATED: a contact with prior (non-synthetic) outreach is preserved and sent to review instead of deleted, and a delete that errors is set aside for a human. Runs only inside flow2, so it inherits the sequence feature gate.',
    emits: 'deleted · has outreach → review · failed → set aside',
  },
  {
    id: 'terminal.deleted',
    kind: 'dead',
    title: 'Deleted',
    subtitle: 'Terminal',
    desc: 'The contact and all its data have been removed. Nobody rests here: the contact’s flow rows cascade-delete with it, so this terminal is the logical end of the delete path (never an actual resting node).',
    emits: '',
  },
  {
    id: 'human.review_identity',
    kind: 'human',
    title: 'Review identity',
    subtitle: 'Human gate',
    desc: 'A profile resolved but the match is ambiguous (name / core mismatch). Parks for a rep to confirm the right person → continue, or reject → set aside. This is a human gate, not just a status.',
    emits: 'confirm → continue · reject → set aside',
  },
  {
    id: 'status.rate_limited',
    kind: 'blocked',
    pill: true,
    title: 'Hit LinkedIn limit',
    subtitle: 'Contact status · account back-off',
    desc: 'LinkedIn temporarily blocked the account for making too many requests too fast (a “rate limit” / HTTP 429).\n\nNo action needed — the account waits out an escalating cooldown (30m → 1h → 2h) and the “Resume waiting contacts” cron retries. It clears on its own.',
    emits: '',
  },
  {
    id: 'status.set_aside',
    kind: 'blocked',
    pill: true,
    title: 'Failed to fetch LinkedIn',
    subtitle: 'Contact status · terminal',
    desc: 'Sets the contact’s status to “failed to fetch linkedin”. The fetch couldn’t complete for this contact — unreachable, or transient errors that exhausted retries — and it isn’t one of the fixable cases. Terminal, flagged for a human.',
    emits: '',
  },
  {
    id: 'status.needs_salesnav',
    kind: 'blocked',
    pill: true,
    title: 'Needs Sales Navigator',
    subtitle: 'Contact status · account fix',
    desc: 'The LinkedIn account can’t view this profile because it has no Sales Navigator subscription. Account-level — parks until someone upgrades the account, then resumes.',
    emits: '',
  },
  {
    id: 'alert.unipile_misconfigured',
    kind: 'effect',
    notify: true,
    title: 'Slack: LinkedIn API misconfigured',
    subtitle: 'Alert ops · system-wide',
    desc: 'Sends a Slack message to ops: our LinkedIn API key is invalid or missing, so EVERY contact’s fetch fails — not just this one. Ops must fix the key; the system pauses until then. Not the contact’s fault.',
    emits: '',
  },
  {
    id: 'action.reconnect_linkedin',
    kind: 'human',
    title: 'Prompt user to reconnect LinkedIn',
    subtitle: 'User action needed',
    desc: 'Shows the user a prompt to reconnect their LinkedIn account. Parks until they reconnect, then research can resume.',
    emits: '',
  },
  {
    id: 'action.add_salesnav',
    kind: 'human',
    title: 'Prompt user to add Sales Navigator',
    subtitle: 'User action needed',
    desc: 'Shows the user a prompt to add a Sales Navigator subscription (needed to view this profile). Parks until they upgrade, then research can resume.',
    emits: '',
  },
  {
    id: 'status.rate_limited_tavily',
    kind: 'blocked',
    pill: true,
    title: 'Hit Tavily limit',
    subtitle: 'Contact status · rate-limit back-off',
    desc: 'Tavily rate-limited us (429). Sets the contact’s status to “hit tavily limit” and parks on a back-off — a distinct wait from slot contention. Its own cron picks it up after the cooldown and re-attempts Acquire Tavily slot.',
    emits: '',
  },
  {
    id: 'cron.sweep_ratelimit_unipile',
    kind: 'guard',
    cron: true,
    title: 'Resume rate-limited (LinkedIn API)',
    subtitle: 'cron · rate-limit back-off',
    desc: 'Separate from the slot-wait sweep: picks up contacts parked at “Hit LinkedIn limit” whose rate-limit cooldown has elapsed and re-enqueues them to re-attempt Acquire LinkedIn API slot.',
    emits: 're-enqueue rate-limited → Acquire LinkedIn API slot',
  },
  {
    id: 'cron.sweep_ratelimit_tavily',
    kind: 'guard',
    cron: true,
    title: 'Resume rate-limited (tavily)',
    subtitle: 'cron · rate-limit back-off',
    desc: 'Separate from the slot-wait sweep: picks up contacts parked at “Hit Tavily limit” whose rate-limit cooldown has elapsed and re-enqueues them to re-attempt Acquire Tavily slot.',
    emits: 're-enqueue rate-limited → Acquire Tavily slot',
  },
  {
    id: 'status.web_search_failed',
    kind: 'effect',
    pill: true,
    title: 'Web search failed',
    subtitle: 'Contact status',
    desc: 'The Tavily web search errored (bad API key / timeout / quota). Records the error on the contact but does NOT strand it — the journey continues to the dossier, synthesized from the profile alone.',
    emits: 'continue → Synthesize dossier',
  },
  {
    id: 'qualify.rules',
    kind: 'filter',
    title: 'Filter rules',
    subtitle: 'Rep-configured rules · both paths',
    desc: 'The deterministic, rep-configured filter rules (evaluateFilterRules) over the folded profile + web dossier — runs for EVERY staged candidate, auto-loaded OR manual. Free, no AI. Reached whether the web search found results or nothing. Pass → ICP qualify; fail → Disqualified (filter_failed).',
    emits: 'pass → ICP qualify · fail → Disqualified',
  },
  {
    id: 'qualify.icp',
    kind: 'filter',
    title: 'ICP qualify',
    subtitle: 'AI · auto-loader campaigns only',
    desc: 'The AI ICP verdict (claudeAiService.qualifyCandidate) over the dossier + profile against the campaign’s audience / avoid + sourcing context. ONLY runs for auto-loader campaigns — pgTemplateIcp returns null unless `auto_load_enabled` and an `audience` is set, so manual / non-auto-loader candidates skip this stage entirely and promote straight off the rule engine. Verdict: yes → Enrolled · no → Disqualified (icp_disqualified, permanent) · unsure → parked for a human review.',
    emits: 'yes → Enrolled · no → Disqualified · unsure → human review',
    ai: {
      modelKey: 'candidate_icp_qualify',
      input:
        'The synthesized dossier + profile + the campaign audience / avoid + sourcing context → verdict yes / no / unsure, with a reason.',
    },
  },
  {
    id: 'dossier.synthesize',
    kind: 'effect',
    title: 'Synthesize dossier',
    subtitle: 'AI · profile + web results',
    desc: 'Claude reads the stashed Tavily web results + the LinkedIn profile and writes the research dossier (extracted fields — background, career highlights, recent + wealth signals). Holds no slot; keeps the stash across retries so it never re-runs the paid search. Runs BEFORE qualification because the ICP verdict reads this dossier (fetchContactDossier). A transient Claude timeout/5xx backs off and retries; a missing/invalid Anthropic key or out-of-credits pauses the loader.',
    emits: 'dossier written → Filter rules · failed → Dossier synthesis failed',
    ai: {
      modelKey: 'contact_dossier_synthesis',
      input:
        'The fetched profile + the stashed Tavily web results + dossier instructions → writes the research dossier (extracted fields).',
    },
  },
  {
    id: 'status.synthesize_failed',
    kind: 'dead',
    pill: true,
    title: 'Dossier synthesis failed',
    subtitle: 'Candidate status · terminal',
    desc: 'The AI dossier synthesis failed unrecoverably (Claude timeout/5xx after its retry budget, or a missing/invalid Anthropic key / out of credits). Sets status = `synthesize_failed`. No outreach happened; re-addable (rejected_at is NULL).',
    emits: '',
  },
  {
    id: 'status.pending_review',
    kind: 'blocked',
    pill: true,
    title: 'Pending review',
    subtitle: 'Contact status (user + team)',
    desc: 'Sets the contact’s status to “pending review” — an ambiguous profile match awaits a rep’s decision, so it shows on the review queue. Then parks at the Review identity gate.',
    emits: '',
  },
  {
    id: 'status.gathering_sources',
    kind: 'effect',
    pill: true,
    title: 'Gathering sources',
    subtitle: 'Contact status (user + team)',
    desc: 'Sets the contact’s status to “gathering sources”, then runs the Tavily web search.',
    emits: '',
  },
  {
    id: 'status.enrolled',
    kind: 'done',
    pill: true,
    title: 'Enrolled',
    subtitle: 'Contact status (user + team)',
    desc: 'The contact qualified (ICP / rules). Sets the contact’s status to “enrolled” — it enters the outreach sequence.',
    emits: '',
  },
  // ── Personalized draft-review lane: AI drafts the per-contact messages, a rep approves/rejects them in
  //    the review queue (sequence.reviewList/reviewDecide) BEFORE the contact enrolls. Drawn so a contact
  //    parked mid-review (or approved/rejected) shows a face here instead of dropping off the graph. Fed
  //    from qualify.icp's personalized branch (undrawn until the engine routes pass_personalized). ──
  {
    id: 'seq.draft',
    kind: 'effect',
    title: 'Draft messages',
    subtitle: 'AI personalization',
    desc: 'For a personalized sequence, Claude drafts the per-contact message for each content step from the dossier, then parks the contact for a rep to approve. If every draft errors, the contact ends errored.',
    emits: 'drafts ready → Review · all failed → errored',
  },
  {
    id: 'human.review_drafts',
    kind: 'human',
    title: 'Review drafts',
    subtitle: 'Rep approval gate',
    desc: 'The contact waits here while a rep reviews the AI-drafted messages in the review queue. Approve → enroll and start sending; reject → end the contact at “rejected”. Resumed by the review decision, not the engine.',
    emits: 'approve → Enroll · reject → Rejected',
  },
  {
    id: 'seq.enroll',
    kind: 'effect',
    pill: true,
    title: 'Enroll',
    subtitle: 'Drafts approved',
    desc: 'The rep approved the drafts. Binds the contact to the sequence and hands off to the step runner (same as the non-personalized “Enrolled” path).',
    emits: 'bound → run steps',
  },
  {
    id: 'terminal.rejected',
    kind: 'dead',
    pill: true,
    title: 'Rejected',
    subtitle: 'Contact status · terminal',
    desc: 'The rep rejected the AI drafts in review. The contact ends here and is not worked (permanent for this enrollment).',
    emits: '',
  },
  {
    id: 'seq.run_step',
    kind: 'guard',
    title: 'Run step',
    subtitle: 'Cursor · dispatch by type',
    desc: 'The sequence step driver: reads the cadence’s current step from the cursor and dispatches by TYPE. Each step runs, then returns here to advance the cursor — so the order is cadence DATA, not hardcoded nodes. Step types: call, reminder, message, connect, inmail, email, wait (`delay`), like (`likeRecentPosts`), follow. call / reminder just create a rep to-do and advance. No more steps → Finished.',
    emits: 'call · reminder · message · connect · inmail · email · wait · like · follow · no more steps → Finished',
  },
  {
    id: 'seq.validate',
    kind: 'guard',
    title: 'Validate step',
    subtitle: 'Cheap pre-send checks · no network',
    desc: 'A fast, NETWORK-FREE gate before a touch send: resolves the step’s content against the contact’s fields and catches unresolved template variables (a `{{field}}` with no value) BEFORE waiting for the working-hours window or spending a LinkedIn API fetch. Unresolved → parks on “Unresolved variables” for a human to fill the field; otherwise → check working hours. Profile-dependent checks (1st-degree, recipient) stay in the atomic send step, which fetches the live profile once.',
    emits: 'valid → check hours · unresolved variables → needs a human',
  },
  {
    id: 'seq.hours_gate',
    kind: 'guard',
    title: 'Check working hours',
    subtitle: 'Send window · per channel',
    desc: 'Before a touch send, verifies the current time is inside the rep’s working-hours window for that channel — LinkedIn steps (message/connect/inmail/like/follow) use the `linkedin` calendar, email uses the `email` calendar (user_team_run_calendar). Inside the window → dispatch the step; outside → Waiting for working hours (deferred, never sent off-hours). The rep’s daily-send cap for the action is enforced at the same gate.',
    emits: 'in hours → dispatch · outside → Waiting for working hours',
  },
  {
    id: 'seq.step.call',
    kind: 'effect',
    title: 'Call',
    subtitle: 'call step',
    desc: 'Creates a rep to-do (a `call` reminder) for the contact, then PARKS the contact on “Waiting on call” — the sequence does not advance until a human logs the call done.',
    emits: 'to-do created → Waiting on call',
  },
  {
    id: 'seq.step.reminder',
    kind: 'effect',
    title: 'Reminder',
    subtitle: 'reminder step',
    desc: 'Creates a rep to-do (a reminder) for the contact, then PARKS the contact on “Waiting on reminder” — the sequence does not advance until a human marks the reminder done.',
    emits: 'to-do created → Waiting on reminder',
  },
  {
    id: 'seq.step.message',
    kind: 'effect',
    title: 'Send message',
    subtitle: 'message · after connect',
    desc: 'Sends the LinkedIn message via the LinkedIn API (effect_key idempotent); requires a 1st-degree connection. A reply interrupts the sequence (Responded). Advances the cursor on send.',
    emits: 'advance → Run step',
  },
  {
    id: 'seq.step.connect',
    kind: 'effect',
    title: 'Send connect',
    subtitle: 'connect · LinkedIn invite',
    desc: 'Sends the connection request via the LinkedIn API (effect_key idempotent). At send it re-checks: already 1st-degree → skip + advance; otherwise → Await acceptance. The weekly invite cap parks until reset.',
    emits: 'sent → Await acceptance',
  },
  {
    id: 'seq.await_connect',
    kind: 'signal',
    title: 'Await acceptance',
    subtitle: 'Poll-or-push · X days',
    desc: 'Waits for the invite to be accepted — via the connection-accepted webhook (push) or a periodic re-check (poll). Deadline is the step’s configured delay (X days). Accepted → advance; not accepted within X days → No connection.',
    emits: 'accepted → Run step · not accepted → No connection',
  },
  {
    id: 'seq.step.inmail',
    kind: 'effect',
    title: 'Send InMail',
    subtitle: 'inmail · Sales Nav',
    desc: 'Sends a LinkedIn InMail via the LinkedIn API — needs a Sales Navigator seat + credits and a non-connected recipient. Out of credits parks until the weekly refill. Advances the cursor on send.',
    emits: 'advance → Run step',
  },
  {
    id: 'seq.step.email',
    kind: 'effect',
    title: 'Send email',
    subtitle: 'email step',
    desc: 'Sends the `email` step for the contact, then advances the cursor.',
    emits: 'advance → Run step',
  },
  {
    id: 'seq.wait',
    kind: 'wait',
    title: 'Wait (delay)',
    subtitle: 'Durable inter-step timer',
    desc: 'A durable delay step (`delay` type): next_run_at = now + the step’s configured delay. No worker sleeps — the row is invisible to the scheduler until then, so it survives deploys/crashes. Then advances the cursor.',
    emits: 'wait until T → Run step',
  },
  {
    id: 'seq.step.like',
    kind: 'effect',
    title: 'Like recent post',
    subtitle: 'likeRecentPosts · LinkedIn API',
    desc: 'Likes the contact’s most-recent post via the LinkedIn API (fetch posts + add reaction). A public pre-connection touch, needs no connection. Benign failures (no posts / rejected / daily cap) skip and advance.',
    emits: 'advance → Run step',
  },
  {
    id: 'seq.step.follow',
    kind: 'effect',
    title: 'Follow',
    subtitle: 'follow · LinkedIn API',
    desc: 'Follows the prospect via the LinkedIn API (a real call → can fail; benign failures skip and advance). Needs no connection. Never un-followed at journey end — a deliberate leave-in-place.',
    emits: 'advance → Run step',
  },
  {
    id: 'terminal.finished',
    kind: 'done',
    pill: true,
    title: 'Finished',
    subtitle: 'Sequence complete',
    desc: 'Terminal success: no more steps on the cursor. next_run_at is NULL; frees the one-live-journey slot so the contact can be re-enrolled later.',
    emits: '',
  },
  // ── Working-hours defer lane: a touch send outside the rep’s window parks here (not a send error) ──
  {
    id: 'seq.waiting_hours',
    kind: 'blocked',
    title: 'Waiting for working hours',
    subtitle: 'Deferred · off-hours',
    desc: 'The touch send fell outside the rep’s working-hours window for that channel, so the arm parks here instead of sending off-hours. The cursor hasn’t advanced — the node-agnostic due-work sweep re-enqueues it (~15m) and the step engine re-checks the window, dispatching the SAME step once it reopens.',
    emits: 'window reopens → re-check + dispatch',
  },
  // ── Shared step error-handling targets (each step routes its failure modes here) ──
  {
    id: 'blocked.send',
    kind: 'blocked',
    title: 'Blocked · can’t send',
    subtitle: 'Slot / transient retry',
    desc: 'A send is gated — no free slot (waits ~60s) or a per-call transient 429/5xx that auto-retries (15m→30m→60m, ×3). Retries the SAME step (the cursor hasn’t advanced, so re-dispatch re-runs it). A disconnected account → Reconnect LinkedIn instead. (Off-hours deferral is its own lane, Waiting for working hours.)',
    emits: 'slot → wait · transient 429/5xx → auto-retry ×3 · retry → Run step',
  },
  {
    id: 'seq.setup_unipile',
    kind: 'human',
    title: 'Reconnect LinkedIn',
    subtitle: 'Account disconnected',
    desc: 'A send hit account_disconnection (expired credentials / checkpoint). The active step pauses and the chain stops — it resumes automatically when the user reconnects the LinkedIn account. Not a dead-letter, not a timed retry.',
    emits: 'reconnected → resume active step',
  },
  {
    id: 'terminal.errored',
    kind: 'dead',
    title: 'Needs attention',
    subtitle: 'Interrupt / hold',
    desc: 'A per-contact step interrupt that needs a human on THIS contact but lets the rest of the cadence continue: message to a non-1st-degree contact, a template-eval failure, a member-id decode failure, an already-connected / no-Sales-Nav InMail, or a per-call error that exhausted its retries. Never silently stuck — the row + ledger carry the node + reason.',
    emits: 'status=errored / interrupted (node + reason)',
  },
  {
    id: 'blocked.invite_cap',
    kind: 'blocked',
    title: 'Invite limit reached',
    subtitle: 'LinkedIn weekly cap',
    desc: 'LinkedIn’s WEEKLY invitation cap was hit (isConnectProviderLimit). Parks until the weekly quota resets — it must NOT dead-letter — then retries Send connect.',
    emits: 'retry → Run step (after weekly reset)',
  },
  {
    id: 'blocked.inmail_credits',
    kind: 'blocked',
    title: 'InMail credits out',
    subtitle: 'Weekly token refill',
    desc: 'The Sales-Navigator InMail credits are exhausted (insufficient_credits → sales_nav_tokens = 0). Parks until the WEEKLY token refill — TOKEN-gated, not time-gated — then retries Send InMail.',
    emits: 'retry → Run step (after weekly token refill)',
  },
  {
    id: 'terminal.no_connect',
    kind: 'dead',
    title: 'No connection',
    subtitle: 'Declined / not accepted',
    desc: 'The invite was declined, or never accepted within the step’s configured X-day deadline. The sequence ends here rather than messaging a non-connection. If the connect step has auto-withdraw, the pending invite is separately reclaimed by the withdraw sweep.',
    emits: 'halt(no_connect)',
  },
  {
    id: 'terminal.responded',
    kind: 'done',
    title: 'Responded',
    subtitle: 'They replied',
    desc: 'The contact replied, so the sequence stops before the next touch (the reply-interrupt guard). A good outcome — recorded with the reply that triggered it.',
    emits: 'halt(responded)',
  },
  {
    id: 'terminal.suppressed',
    kind: 'dead',
    title: 'Suppressed',
    subtitle: 'Do-not-contact',
    desc: 'Terminal stop for a contact we must not message — a rep set the do-not-contact toggle, or a review confirmed a possible opt-out. Ledgered with actor + reason; the one-live-journey slot is freed.',
    emits: 'halt(suppressed)',
  },
  {
    id: 'seq.unresolved_vars',
    kind: 'human',
    title: 'Unresolved variables',
    subtitle: 'Missing merge field · rep fixes',
    desc: 'The step body/subject has a template variable that couldn’t be filled — an `@variable` with no value, or a leftover `{token}` — so the send is BLOCKED before it goes out; the content is NEVER sent half-filled. Evaluated at send time by unversalStepEvaluate: sets cadence_step.state = `interrupted`, body2_error = `unresolved variable` (error code `unresolved_variables`), evaluated_body = null. Same guard for message / connect / inmail / email. A rep fills the missing field and retries → the step re-evaluates and re-runs.',
    emits: 'rep fills field + retry → Run step',
  },
  // ── Manual-task park lanes: a call / reminder step created a rep to-do; the contact rests here until a
  //    human logs the to-do done, which resumes the arm past the step. ──
  {
    id: 'status.call_waiting',
    kind: 'human',
    title: 'Waiting on call',
    subtitle: 'Rep to-do · call logged',
    desc: 'The `call` step created a rep to-do (a `global_reminder`) and the contact rests here — the sequence never auto-advances past a manual call. When a rep logs the call done (or clears the to-do), the arm advances to the next step. Only a contact reply cancels the whole enrollment.',
    emits: 'call logged done → Run step',
  },
  {
    id: 'status.reminder_waiting',
    kind: 'human',
    title: 'Waiting on reminder',
    subtitle: 'Rep to-do · marked done',
    desc: 'The `reminder` step created a rep to-do (a `global_reminder`) and the contact rests here — the sequence never auto-advances past a manual reminder. When a rep marks it done (or clears the to-do), the arm advances to the next step. Only a contact reply cancels the whole enrollment.',
    emits: 'reminder marked done → Run step',
  },
  {
    id: 'status.missing_content',
    kind: 'human',
    title: 'Missing content',
    subtitle: 'Required field empty · rep fixes',
    desc: 'A send step is missing a REQUIRED field — an empty message/connect body, or an InMail/email with no subject or body — so it can’t go out. Distinct from “Unresolved variables” (a merge field that didn’t resolve): here the step content itself is blank. A rep fills in the step content and retries → the step re-validates and re-runs.',
    emits: 'content filled + retry → Run step',
  },
  {
    id: 'seq.reply_watch',
    kind: 'signal',
    title: 'Reply received',
    subtitle: 'Webhook / poll · interrupts',
    desc: 'Ambient response detection that watches the WHOLE running sequence. A LinkedIn message / InMail reply arrives via the LinkedIn API `message_received` webhook (real-time); an email reply is found by the Gmail sync poll (with latency). On any inbound reply it stamps cadence.state = `responded` + reply_on and sets contact_user.responded = true — and the step runner then skips ALL remaining steps. So a reply on any channel, during any step, interrupts the flow.',
    emits: 'inbound reply (any channel / any step) → Responded',
  },
  {
    id: 'seq.fork',
    kind: 'fork',
    title: 'Fan-out',
    subtitle: 'Parallel group · AND-join',
    desc: 'A parallel-group step (A → [B, C, D] → E): N authored arms run SIMULTANEOUSLY — the engine lets a contact occupy multiple nodes at once, one live arm each. The Join waits for ALL arms (AND-barrier) before the main line continues to E. A global outcome in any arm (replied / suppressed / no-connection) ends the whole journey rather than waiting at the join.',
    emits: 'fan-out → each arm’s first step · all arms done → Join',
  },
  {
    id: 'seq.join',
    kind: 'filter',
    title: 'Join',
    subtitle: 'AND-barrier · all arms',
    desc: 'The barrier after a Fan-out: advances the main line only once EVERY arm has completed. The clock at the join is the slowest arm (the max of the arms’ end-times) — the whole group takes as long as its longest branch.',
    emits: 'all arms complete → advance the main line',
  },
  {
    id: 'seq.spawn',
    kind: 'fork',
    title: 'Spawn branch',
    subtitle: 'Detached · no join',
    desc: 'Starts an INDEPENDENT arm alongside the main line and returns immediately — the main sequence keeps going with NO barrier (A → B, A → C → D, A → E: after A, B/C/E all trigger; when C finishes, D triggers; nothing waits). Each spawned arm runs to its own outcome; it never ends or changes the main journey’s terminal.',
    emits: 'spawn detached arm · main line → next step',
  },
  {
    id: 'seq.switch',
    kind: 'fork',
    title: 'Switch',
    subtitle: 'Route by age / AUM · one branch',
    desc: 'Evaluates a numeric contact attribute (Age or ~AUM) and routes the contact into EXACTLY ONE matching branch (half-open [min, max) ranges, first-match-wins, optional catch-all). That branch starts as its own arm and runs to its own outcome; the main line ends here — each branch is a terminal path with no rejoin. If nothing matches and there is no catch-all, the journey simply ends.',
    emits: 'route → matched branch (terminal) · no match → end',
  },
  {
    id: 'status.disqualified',
    kind: 'dead',
    pill: true,
    title: 'Disqualified',
    subtitle: 'Contact status (user + team)',
    desc: 'The contact did not qualify (ICP / rules). Sets the contact’s status to “disqualified” — terminal, it is not contacted.',
    emits: '',
  },
];

// "Never queried" and "Stale" both go to "Is unipile enabled"; "Fresh" is unconnected.
// "Is unipile enabled" has 3 outputs, all unconnected for now (empty `to` → amber row).
// Distinct ids let two edges share a target.
export const FLOW2_EDGES: HelmFlowEdge[] = [
  // ── Auto-loader intake lane: cron → pull → stage → recontact gate → title screen.
  //    The gates' reject rows and the title-screen "fits ICP" pass are unconnected for
  //    now (amber rows) — the pass will later wire into "Should query unipile". ──
  { from: 'loader.cron', to: 'loader.pull', label: 'runway < 3 days' },
  { from: 'loader.pull', to: 'loader.stage', label: 'imported (≤200/search)' },
  { from: 'loader.stage', to: 'recontact.gate', label: 'each candidate (pending)' },
  { from: 'recontact.gate', to: 'title.screen', id: 'recontact.gate:pass', label: 'Not contacted before' },
  {
    from: 'recontact.gate',
    to: 'status.replied_teammate',
    id: 'recontact.gate:engaged',
    label: 'Replied to a teammate',
    kind: 'skip',
  },
  {
    from: 'recontact.gate',
    to: 'status.already_contacted',
    id: 'recontact.gate:contacted',
    label: 'You already contacted',
    kind: 'skip',
  },
  { from: 'title.screen', to: 'research.should', id: 'title.screen:pass', label: 'Fits ICP → research' },
  { from: 'title.screen', to: 'status.title_disqualified', id: 'title.screen:reject', label: 'Off-ICP', kind: 'skip' },
  { from: 'seq.add', to: 'seq.dupe_check', label: 'enroll' },
  { from: 'seq.dupe_check', to: 'status.adding', id: 'seq.dupe_check:clear', label: 'Not already in outreach' },
  {
    from: 'seq.dupe_check',
    to: 'status.already_active',
    id: 'seq.dupe_check:duplicate',
    label: 'Already in a sequence / campaign / switch',
    kind: 'skip',
  },
  { from: 'status.adding', to: 'research.should' },
  { from: 'research.should', to: 'unipile.enabled', id: 'research.should:never', label: 'Never queried LinkedIn API' },
  {
    from: 'research.should',
    to: 'unipile.enabled',
    id: 'research.should:stale',
    label: 'Stale · queried LinkedIn API > 1 day ago',
  },
  {
    from: 'research.should',
    to: 'title.rescreen',
    id: 'research.should:fresh',
    label: 'Fresh · queried LinkedIn API < 1 day ago',
    kind: 'skip',
  },
  { from: 'unipile.enabled', to: 'unipile.slot', id: 'unipile.enabled:success', label: 'Success' },
  {
    from: 'unipile.enabled',
    to: 'unipile.disconnected',
    id: 'unipile.enabled:disconnected',
    label: 'LinkedIn API not connected',
  },
  { from: 'unipile.slot', to: 'status.researching', label: 'slot acquired' },
  { from: 'unipile.slot', to: 'status.waiting_slot', label: 'slot full', kind: 'block' },
  { from: 'cron.sweep', to: 'unipile.slot', label: 're-enqueue', kind: 'resume' },
  { from: 'cron.sweep_ratelimit_unipile', to: 'unipile.slot', label: 're-enqueue', kind: 'resume' },
  { from: 'status.researching', to: 'unipile.fetch' },
  // The fetch holds the slot; the moment it returns, release the slot BEFORE routing on the outcome
  // — so every branch below is post-release (the slot is never held while parked/errored).
  { from: 'unipile.fetch', to: 'unipile.slot.release', label: 'query returns' },
  { from: 'unipile.slot.release', to: 'title.rescreen', id: 'unipile.fetch:success', label: 'Success' },
  { from: 'title.rescreen', to: 'tavily.slot', id: 'title.rescreen:pass', label: 'Fits ICP → web search' },
  {
    from: 'title.rescreen',
    to: 'status.title_disqualified',
    id: 'title.rescreen:reject',
    label: 'Off-ICP',
    kind: 'skip',
  },
  { from: 'tavily.slot', to: 'status.gathering_sources', label: 'slot acquired' },
  { from: 'status.gathering_sources', to: 'tavily.search' },
  { from: 'tavily.slot', to: 'status.waiting_tavily', label: 'slot full', kind: 'block' },
  { from: 'cron.sweep_tavily', to: 'tavily.slot', label: 're-enqueue', kind: 'resume' },
  { from: 'tavily.search', to: 'tavily.slot.release', label: 'search returns' },
  { from: 'cron.sweep_ratelimit_tavily', to: 'tavily.slot', label: 're-enqueue', kind: 'resume' },
  {
    from: 'unipile.slot.release',
    to: 'contact.delete',
    id: 'unipile.fetch:unavailable',
    label: 'No LinkedIn profile',
    kind: 'fail',
  },
  // No-LinkedIn contact → the marker forwards into the explicit delete executor, which hard-deletes it (or
  // preserves + routes to review when the safety gate trips / the delete errors).
  {
    from: 'contact.delete',
    to: 'contact.purge',
    id: 'contact.delete:next',
    label: 'no profile → delete',
    kind: 'fail',
  },
  { from: 'contact.purge', to: 'terminal.deleted', id: 'contact.purge:deleted', label: 'deleted', kind: 'fail' },
  {
    from: 'contact.purge',
    to: 'status.pending_review',
    id: 'contact.purge:skipped',
    label: 'has outreach → review',
    kind: 'human',
  },
  {
    from: 'contact.purge',
    to: 'status.set_aside',
    id: 'contact.purge:failed',
    label: 'delete failed → set aside',
    kind: 'fail',
  },
  {
    from: 'unipile.slot.release',
    to: 'status.pending_review',
    id: 'unipile.fetch:review',
    label: 'Needs identity review',
    kind: 'human',
  },
  { from: 'status.pending_review', to: 'human.review_identity', label: 'awaiting rep' },
  {
    from: 'unipile.slot.release',
    to: 'unipile.disconnected',
    id: 'unipile.fetch:disconnected',
    label: 'Account disconnected',
    kind: 'block',
  },
  {
    from: 'unipile.slot.release',
    to: 'status.rate_limited',
    id: 'unipile.fetch:ratelimited',
    label: 'Hit LinkedIn limit',
    kind: 'block',
  },
  {
    from: 'unipile.slot.release',
    to: 'status.needs_salesnav',
    id: 'unipile.fetch:no_salesnav',
    label: 'No Sales Navigator',
    kind: 'block',
  },
  {
    from: 'unipile.slot.release',
    to: 'alert.unipile_misconfigured',
    id: 'unipile.fetch:misconfigured',
    label: 'LinkedIn API misconfigured',
    kind: 'fail',
  },
  {
    from: 'unipile.slot.release',
    to: 'status.set_aside',
    id: 'unipile.fetch:failed',
    label: 'Couldn’t fetch (gave up)',
    kind: 'fail',
  },
  // Account-fix prompts after the parked statuses.
  { from: 'unipile.disconnected', to: 'action.reconnect_linkedin', label: 'prompt reconnect', kind: 'resume' },
  { from: 'status.needs_salesnav', to: 'action.add_salesnav', label: 'prompt upgrade', kind: 'resume' },
  // "Tavily search the web" outcomes: results/none → Synthesize dossier · rate-limit → status · API error → status.
  { from: 'tavily.slot.release', to: 'dossier.synthesize', id: 'tavily.search:success', label: 'Success' },
  { from: 'tavily.slot.release', to: 'dossier.synthesize', id: 'tavily.search:no_results', label: 'No results' },
  {
    from: 'tavily.slot.release',
    to: 'status.rate_limited_tavily',
    id: 'tavily.search:ratelimited',
    label: 'Rate limited',
    kind: 'block',
  },
  {
    from: 'tavily.slot.release',
    to: 'status.web_search_failed',
    id: 'tavily.search:failed',
    label: 'API error',
    kind: 'fail',
  },
  // A web-search failure is recorded but non-fatal — continue to the dossier (built from the profile alone).
  {
    from: 'status.web_search_failed',
    to: 'dossier.synthesize',
    id: 'web_search_failed:continue',
    label: 'Continue anyway',
  },
  // Synthesize the dossier BEFORE qualification (the ICP verdict reads it), then the two-stage
  // qualify: rep-configured rules (both paths) → AI ICP qualify (auto-loader only) → Enrolled.
  { from: 'dossier.synthesize', to: 'qualify.rules', id: 'dossier.synthesize:success', label: 'Dossier written' },
  {
    from: 'dossier.synthesize',
    to: 'status.synthesize_failed',
    id: 'dossier.synthesize:failed',
    label: 'Synthesis failed',
    kind: 'fail',
  },
  { from: 'qualify.rules', to: 'qualify.icp', id: 'qualify.rules:pass', label: 'Passes rules' },
  { from: 'qualify.rules', to: 'status.disqualified', id: 'qualify.rules:fail', label: 'Filtered out', kind: 'skip' },
  { from: 'qualify.icp', to: 'status.enrolled', id: 'qualify.icp:yes', label: 'Qualified (yes)' },
  { from: 'qualify.icp', to: 'status.disqualified', id: 'qualify.icp:no', label: 'Disqualified (no)', kind: 'skip' },
  // Unsure verdict parks for a human review — unconnected for now (the review target is a
  // separate decision; wiring it to the far-right Pending review would be a long crossing edge).
  { from: 'qualify.icp', to: '', id: 'qualify.icp:unsure', label: 'Unsure → human review', kind: 'human' },
  // Enrolled hands off to the sequence runner, which dispatches each step by type
  // (order is cadence data). Step targets are unwired for now → amber rows.
  { from: 'status.enrolled', to: 'seq.run_step', label: 'bound → run steps' },
  // Personalized draft-review lane (fed from qualify.icp's undrawn `personalized` branch).
  { from: 'seq.draft', to: 'human.review_drafts', id: 'seq.draft:ready', label: 'Drafts ready → review' },
  { from: 'seq.draft', to: 'terminal.errored', id: 'seq.draft:failed', label: 'Every draft errored', kind: 'fail' },
  {
    from: 'human.review_drafts',
    to: 'seq.enroll',
    id: 'human.review_drafts:approve',
    label: 'Approve → enroll',
    kind: 'resume',
  },
  {
    from: 'human.review_drafts',
    to: 'terminal.rejected',
    id: 'human.review_drafts:reject',
    label: 'Reject',
    kind: 'fail',
  },
  { from: 'seq.enroll', to: 'seq.run_step', id: 'seq.enroll:bound', label: 'bound → run steps' },
  { from: 'seq.run_step', to: 'seq.step.call', id: 'seq.run_step:call', label: 'Call' },
  { from: 'seq.run_step', to: 'seq.step.reminder', id: 'seq.run_step:reminder', label: 'Reminder' },
  { from: 'seq.run_step', to: 'seq.step.message', id: 'seq.run_step:message', label: 'Message' },
  { from: 'seq.run_step', to: 'seq.step.connect', id: 'seq.run_step:connect', label: 'Connect' },
  { from: 'seq.run_step', to: 'seq.step.inmail', id: 'seq.run_step:inmail', label: 'InMail' },
  { from: 'seq.run_step', to: 'seq.step.email', id: 'seq.run_step:email', label: 'Email' },
  { from: 'seq.run_step', to: 'seq.wait', id: 'seq.run_step:wait', label: 'Wait' },
  { from: 'seq.run_step', to: 'seq.step.like', id: 'seq.run_step:like', label: 'Like' },
  { from: 'seq.run_step', to: 'seq.step.follow', id: 'seq.run_step:follow', label: 'Follow' },
  { from: 'seq.run_step', to: 'terminal.finished', id: 'seq.run_step:done', label: 'No more steps' },
  // Pre-send working-hours gate: a touch send checks the rep’s window before dispatch; outside it, defer.
  {
    from: 'seq.run_step',
    to: 'seq.validate',
    id: 'seq.run_step:send_window',
    label: 'Touch send → validate',
    kind: 'skip',
  },
  { from: 'seq.validate', to: 'seq.hours_gate', id: 'seq.validate:ok', label: 'Valid → check hours' },
  {
    from: 'seq.validate',
    to: 'seq.unresolved_vars',
    id: 'seq.validate:unresolved',
    label: 'Unresolved variables',
    kind: 'human',
  },
  {
    from: 'seq.validate',
    to: 'status.missing_content',
    id: 'seq.validate:missing_content',
    label: 'Required field empty',
    kind: 'human',
  },
  {
    from: 'seq.hours_gate',
    to: 'seq.run_step',
    id: 'seq.hours_gate:open',
    label: 'In hours → dispatch',
    kind: 'resume',
  },
  {
    from: 'seq.hours_gate',
    to: 'seq.waiting_hours',
    id: 'seq.hours_gate:closed',
    label: 'Outside hours → defer',
    kind: 'block',
  },
  // Each step returns to the dispatcher to advance the cursor (the runner loop). call / reminder are the
  // exception: they PARK on a status node until a human logs the to-do done (then the arm advances).
  {
    from: 'seq.step.call',
    to: 'status.call_waiting',
    id: 'seq.step.call:waiting',
    label: 'to-do → wait',
    kind: 'human',
  },
  {
    from: 'seq.step.reminder',
    to: 'status.reminder_waiting',
    id: 'seq.step.reminder:waiting',
    label: 'to-do → wait',
    kind: 'human',
  },
  {
    from: 'status.call_waiting',
    to: 'seq.run_step',
    id: 'status.call_waiting:done',
    label: 'call done → advance',
    kind: 'resume',
  },
  {
    from: 'status.reminder_waiting',
    to: 'seq.run_step',
    id: 'status.reminder_waiting:done',
    label: 'reminder done → advance',
    kind: 'resume',
  },
  {
    from: 'status.missing_content',
    to: 'seq.run_step',
    id: 'status.missing_content:retry',
    label: 'content fixed → retry',
    kind: 'resume',
  },
  { from: 'seq.step.message', to: 'seq.run_step', id: 'seq.step.message:advance', label: '↺ advance', kind: 'resume' },
  { from: 'seq.step.connect', to: 'seq.await_connect', id: 'seq.step.connect:sent', label: 'sent' },
  {
    from: 'seq.await_connect',
    to: 'seq.run_step',
    id: 'seq.await_connect:accepted',
    label: '↺ accepted → advance',
    kind: 'resume',
  },
  {
    from: 'seq.await_connect',
    to: 'terminal.no_connect',
    id: 'seq.await_connect:no_connect',
    label: 'Not accepted → No connection',
    kind: 'fail',
  },
  {
    from: 'seq.await_connect',
    to: 'terminal.responded',
    id: 'seq.await_connect:replied',
    label: 'replied',
    kind: 'signal',
  },
  { from: 'seq.step.inmail', to: 'seq.run_step', id: 'seq.step.inmail:advance', label: '↺ advance', kind: 'resume' },
  { from: 'seq.step.email', to: 'seq.run_step', id: 'seq.step.email:advance', label: '↺ advance', kind: 'resume' },
  { from: 'seq.wait', to: 'seq.run_step', id: 'seq.wait:advance', label: '↺ advance', kind: 'resume' },
  { from: 'seq.step.like', to: 'seq.run_step', id: 'seq.step.like:advance', label: '↺ advance', kind: 'resume' },
  { from: 'seq.step.follow', to: 'seq.run_step', id: 'seq.step.follow:advance', label: '↺ advance', kind: 'resume' },
  // ── Step error handling (mirrors the real seq.step.* outcomes) ──
  // Send connect
  {
    from: 'seq.step.connect',
    to: 'seq.run_step',
    id: 'seq.step.connect:already',
    label: 'already connected → advance',
    kind: 'resume',
  },
  {
    from: 'seq.step.connect',
    to: 'blocked.invite_cap',
    id: 'seq.step.connect:invite_cap',
    label: 'weekly invite cap',
    kind: 'block',
  },
  {
    from: 'seq.step.connect',
    to: 'blocked.send',
    id: 'seq.step.connect:blocked',
    label: 'slot / transient',
    kind: 'block',
  },
  {
    from: 'seq.step.connect',
    to: 'seq.setup_unipile',
    id: 'seq.step.connect:account_down',
    label: 'account disconnected',
    kind: 'block',
  },
  // Send message
  { from: 'seq.step.message', to: 'terminal.responded', id: 'seq.step.message:reply', label: 'reply', kind: 'signal' },
  {
    from: 'seq.step.message',
    to: 'terminal.suppressed',
    id: 'seq.step.message:optout',
    label: 'opt-out',
    kind: 'fail',
  },
  {
    from: 'seq.step.message',
    to: 'terminal.errored',
    id: 'seq.step.message:interrupt',
    label: 'not 1st-degree',
    kind: 'fail',
  },
  {
    from: 'seq.step.message',
    to: 'blocked.send',
    id: 'seq.step.message:blocked',
    label: 'slot / transient',
    kind: 'block',
  },
  {
    from: 'seq.step.message',
    to: 'seq.setup_unipile',
    id: 'seq.step.message:account_down',
    label: 'account disconnected',
    kind: 'block',
  },
  // Send InMail
  {
    from: 'seq.step.inmail',
    to: 'terminal.errored',
    id: 'seq.step.inmail:interrupt',
    label: 'already connected / no Sales Nav',
    kind: 'fail',
  },
  {
    from: 'seq.step.inmail',
    to: 'blocked.inmail_credits',
    id: 'seq.step.inmail:no_credits',
    label: 'no credits',
    kind: 'block',
  },
  {
    from: 'seq.step.inmail',
    to: 'blocked.send',
    id: 'seq.step.inmail:blocked',
    label: 'slot / transient',
    kind: 'block',
  },
  {
    from: 'seq.step.inmail',
    to: 'seq.setup_unipile',
    id: 'seq.step.inmail:account_down',
    label: 'account disconnected',
    kind: 'block',
  },
  // Like recent post
  {
    from: 'seq.step.like',
    to: 'blocked.send',
    id: 'seq.step.like:retry',
    label: 'transient 429/5xx → auto-retry',
    kind: 'block',
  },
  {
    from: 'seq.step.like',
    to: 'seq.setup_unipile',
    id: 'seq.step.like:account_down',
    label: 'account disconnected',
    kind: 'block',
  },
  {
    from: 'seq.step.like',
    to: 'terminal.errored',
    id: 'seq.step.like:interrupt',
    label: 'malformed post → interrupt',
    kind: 'fail',
  },
  // Follow
  {
    from: 'seq.step.follow',
    to: 'blocked.send',
    id: 'seq.step.follow:retry',
    label: 'transient 429/5xx → auto-retry',
    kind: 'block',
  },
  {
    from: 'seq.step.follow',
    to: 'seq.setup_unipile',
    id: 'seq.step.follow:account_down',
    label: 'account disconnected',
    kind: 'block',
  },
  {
    from: 'seq.step.follow',
    to: 'terminal.errored',
    id: 'seq.step.follow:interrupt',
    label: 'decode-fail → interrupt',
    kind: 'fail',
  },
  // Call / Reminder / Email
  { from: 'seq.step.call', to: 'terminal.errored', id: 'seq.step.call:failed', label: 'failed', kind: 'fail' },
  { from: 'seq.step.reminder', to: 'terminal.errored', id: 'seq.step.reminder:failed', label: 'failed', kind: 'fail' },
  { from: 'seq.step.email', to: 'terminal.errored', id: 'seq.step.email:failed', label: 'failed', kind: 'fail' },
  // Block / retry resume — cursor hasn't advanced, so re-dispatch re-runs the active step.
  { from: 'blocked.send', to: 'seq.run_step', id: 'blocked.send:retry', label: 'retry → active step', kind: 'resume' },
  {
    from: 'blocked.invite_cap',
    to: 'seq.run_step',
    id: 'blocked.invite_cap:retry',
    label: 'weekly reset → retry',
    kind: 'resume',
  },
  {
    from: 'blocked.inmail_credits',
    to: 'seq.run_step',
    id: 'blocked.inmail_credits:retry',
    label: 'refill → retry',
    kind: 'resume',
  },
  {
    from: 'seq.setup_unipile',
    to: 'seq.run_step',
    id: 'seq.setup_unipile:resume',
    label: 'reconnected → resume',
    kind: 'resume',
  },
  // Unresolved template variables block the send (state=interrupted) for message / connect /
  // inmail / email; a rep fills the field and retries → re-run the active step.
  {
    from: 'seq.step.message',
    to: 'seq.unresolved_vars',
    id: 'seq.step.message:unresolved',
    label: 'unresolved variables',
    kind: 'human',
  },
  {
    from: 'seq.step.connect',
    to: 'seq.unresolved_vars',
    id: 'seq.step.connect:unresolved',
    label: 'unresolved variables',
    kind: 'human',
  },
  {
    from: 'seq.step.inmail',
    to: 'seq.unresolved_vars',
    id: 'seq.step.inmail:unresolved',
    label: 'unresolved variables',
    kind: 'human',
  },
  {
    from: 'seq.step.email',
    to: 'seq.unresolved_vars',
    id: 'seq.step.email:unresolved',
    label: 'unresolved variables',
    kind: 'human',
  },
  {
    from: 'seq.unresolved_vars',
    to: 'seq.run_step',
    id: 'seq.unresolved_vars:retry',
    label: 'field filled → retry',
    kind: 'resume',
  },
  // Response detection — ambient interrupt across the whole sequence (any channel / any step).
  {
    from: 'seq.run_step',
    to: 'seq.reply_watch',
    id: 'seq.run_step:reply_watch',
    label: 'watch inbound (msg / inmail / email)',
    kind: 'signal',
  },
  {
    from: 'seq.reply_watch',
    to: 'terminal.responded',
    id: 'seq.reply_watch:responded',
    label: 'reply detected → interrupt whole flow',
    kind: 'signal',
  },
  // Parallel step types dispatched by the runner: group (fan-out → join) and spawn (detached).
  { from: 'seq.run_step', to: 'seq.fork', id: 'seq.run_step:group', label: 'type: group (parallel)' },
  { from: 'seq.fork', to: 'seq.run_step', id: 'seq.fork:fanout', label: 'fan-out → each arm', kind: 'skip' },
  { from: 'seq.run_step', to: 'seq.join', id: 'seq.run_step:join', label: 'arm complete → barrier', kind: 'resume' },
  { from: 'seq.join', to: 'seq.run_step', id: 'seq.join:advance', label: 'all arms done → advance', kind: 'resume' },
  { from: 'seq.run_step', to: 'seq.spawn', id: 'seq.run_step:spawn', label: 'type: spawn (detached)' },
  {
    from: 'seq.spawn',
    to: 'seq.run_step',
    id: 'seq.spawn:detached',
    label: 'detached arm + main continues',
    kind: 'skip',
  },
  // Switch: route by a numeric attribute (age / AUM) into ONE branch — that branch is its own terminal path.
  { from: 'seq.run_step', to: 'seq.switch', id: 'seq.run_step:switch', label: 'type: switch (route by age/AUM)' },
  {
    from: 'seq.switch',
    to: 'seq.run_step',
    id: 'seq.switch:route',
    label: 'route → matched branch (terminal)',
    kind: 'skip',
  },
];

// Baked baseline layout (from the downloaded helm-flow JSON): fixed positions + edge sides.
// Runtime drags still layer on top (localStorage) and "Reset layout" returns to this.
export const FLOW2_LAYOUT: SavedLayout = {
  nodes: {
    'loader.cron': { x: -4, y: -183, headerH: 26 },
    'loader.pull': { x: 287, y: -161, headerH: 26 },
    'loader.stage': { x: 239, y: -88, headerH: 26 },
    'recontact.gate': {
      x: -47,
      y: -96,
      headerH: 26,
      outOrder: ['recontact.gate:engaged', 'recontact.gate:contacted', 'recontact.gate:pass'],
    },
    'title.screen': { x: 233, y: 11, headerH: 26, outOrder: ['title.screen:pass', 'title.screen:reject'] },
    'status.replied_teammate': { x: -308, y: -83, headerH: 26 },
    'status.already_contacted': { x: -298, y: -41, headerH: 26 },
    'status.title_disqualified': {
      x: 124,
      y: 147,
      headerH: 26,
      inOrder: ['title.screen:reject', 'title.rescreen:reject'],
    },
    'seq.add': { x: 931, y: -221, headerH: 26, w: 253 },
    'seq.dupe_check': { x: 910, y: -108, headerH: 26, outOrder: ['seq.dupe_check:duplicate', 'seq.dupe_check:clear'] },
    'status.already_active': { x: 680, y: -124, headerH: 26 },
    'status.adding': { x: 751, y: -55, headerH: 26 },
    'research.should': {
      x: 556,
      y: -8,
      headerH: 26,
      outOrder: ['research.should:fresh', 'research.should:stale', 'research.should:never'],
      inOrder: ['title.screen:pass', 'status.adding->research.should'],
    },
    'unipile.enabled': {
      x: 558,
      y: 114,
      headerH: 46,
      outOrder: ['unipile.enabled:success', 'unipile.enabled:disconnected'],
      inOrder: ['research.should:never', 'research.should:stale'],
    },
    'unipile.slot': {
      x: 867,
      y: 178,
      headerH: 26,
      outOrder: ['unipile.slot->status.waiting_slot', 'unipile.slot->status.researching'],
      inOrder: ['cron.sweep->unipile.slot', 'cron.sweep_ratelimit_unipile->unipile.slot', 'unipile.enabled:success'],
    },
    'status.researching': { x: 1184, y: 274, headerH: 26 },
    'status.waiting_slot': { x: 1171, y: 208, headerH: 26 },
    'cron.sweep': { x: 864, y: 74, headerH: 26 },
    'unipile.fetch': { x: 813, y: 311, headerH: 26 },
    'unipile.slot.release': {
      x: 800,
      y: 428,
      headerH: 26,
      outOrder: [
        'unipile.fetch:disconnected',
        'unipile.fetch:success',
        'unipile.fetch:unavailable',
        'unipile.fetch:no_salesnav',
        'unipile.fetch:review',
        'unipile.fetch:ratelimited',
        'unipile.fetch:misconfigured',
        'unipile.fetch:failed',
      ],
    },
    'title.rescreen': {
      x: 99,
      y: 228,
      headerH: 26,
      outOrder: ['title.rescreen:reject', 'title.rescreen:pass'],
      inOrder: ['research.should:fresh', 'unipile.fetch:success'],
    },
    'cron.sweep_tavily': { x: -328, y: 257, headerH: 26 },
    'tavily.slot': {
      x: -72,
      y: 381,
      headerH: 26,
      outOrder: ['tavily.slot->status.gathering_sources', 'tavily.slot->status.waiting_tavily'],
      inOrder: ['cron.sweep_tavily->tavily.slot', 'cron.sweep_ratelimit_tavily->tavily.slot', 'title.rescreen:pass'],
    },
    'status.waiting_tavily': { x: -45, y: 471, headerH: 26 },
    'tavily.search': { x: -456, y: 464, headerH: 26 },
    'tavily.slot.release': {
      x: -122,
      y: 557,
      headerH: 26,
      outOrder: [
        'tavily.search:failed',
        'tavily.search:success',
        'tavily.search:no_results',
        'tavily.search:ratelimited',
      ],
    },
    'unipile.disconnected': {
      x: 493,
      y: 251,
      headerH: 26,
      inOrder: ['unipile.enabled:disconnected', 'unipile.fetch:disconnected'],
    },
    'contact.delete': { x: 1163, y: 449, headerH: 26 },
    'contact.purge': {
      x: 1545,
      y: 471,
      headerH: 26,
      outOrder: ['contact.purge:deleted', 'contact.purge:failed', 'contact.purge:skipped'],
    },
    'terminal.deleted': { x: 1558, y: 615, headerH: 26 },
    'human.review_identity': { x: 990, y: 698, headerH: 26 },
    'status.rate_limited': { x: 1134, y: 522, headerH: 26 },
    'status.set_aside': { x: 1164, y: 631, headerH: 26, inOrder: ['unipile.fetch:failed', 'contact.purge:failed'] },
    'status.needs_salesnav': { x: 560, y: 718, headerH: 26 },
    'alert.unipile_misconfigured': { x: 1155, y: 570, headerH: 26 },
    'action.reconnect_linkedin': { x: 407, y: 307, headerH: 26 },
    'action.add_salesnav': { x: 787, y: 746, headerH: 26 },
    'status.rate_limited_tavily': { x: 170, y: 586, headerH: 26 },
    'cron.sweep_ratelimit_unipile': { x: 861, y: -1, headerH: 26 },
    'cron.sweep_ratelimit_tavily': { x: -273, y: 177, headerH: 26 },
    'status.web_search_failed': { x: -366, y: 557, headerH: 26 },
    'qualify.rules': { x: -472, y: 781, headerH: 26, outOrder: ['qualify.rules:fail', 'qualify.rules:pass'] },
    'qualify.icp': {
      x: -483,
      y: 908,
      headerH: 26,
      outOrder: ['qualify.icp:yes', 'qualify.icp:no', 'qualify.icp:unsure'],
    },
    'dossier.synthesize': {
      x: -525,
      y: 603,
      headerH: 46,
      outOrder: ['dossier.synthesize:success', 'dossier.synthesize:failed'],
      inOrder: ['web_search_failed:continue', 'tavily.search:success', 'tavily.search:no_results'],
    },
    'status.synthesize_failed': { x: -506, y: 720, headerH: 26 },
    'status.pending_review': {
      x: 790,
      y: 706,
      headerH: 26,
      inOrder: ['unipile.fetch:review', 'contact.purge:skipped'],
    },
    'status.gathering_sources': { x: -307, y: 411, headerH: 26 },
    'status.enrolled': { x: 47, y: 755, headerH: 26 },
    'seq.draft': { x: -445, y: 1299, headerH: 26, outOrder: ['seq.draft:ready', 'seq.draft:failed'] },
    'human.review_drafts': {
      x: -396,
      y: 1143,
      headerH: 26,
      outOrder: ['human.review_drafts:approve', 'human.review_drafts:reject'],
    },
    'seq.enroll': { x: -8, y: 859, headerH: 26 },
    'terminal.rejected': { x: -384, y: 1245, headerH: 26 },
    'seq.run_step': {
      x: 269,
      y: 976,
      headerH: 46,
      w: 254,
      outOrder: [
        'seq.run_step:call',
        'seq.run_step:reminder',
        'seq.run_step:message',
        'seq.run_step:connect',
        'seq.run_step:inmail',
        'seq.run_step:email',
        'seq.run_step:wait',
        'seq.run_step:like',
        'seq.run_step:follow',
        'seq.run_step:done',
        'seq.run_step:send_window',
        'seq.run_step:reply_watch',
        'seq.run_step:group',
        'seq.run_step:join',
        'seq.run_step:spawn',
        'seq.run_step:switch',
      ],
      inOrder: [
        'status.enrolled->seq.run_step',
        'seq.enroll:bound',
        'seq.hours_gate:open',
        'status.missing_content:retry',
        'seq.step.message:advance',
        'seq.await_connect:accepted',
        'seq.step.inmail:advance',
        'seq.step.email:advance',
        'seq.wait:advance',
        'seq.step.like:advance',
        'seq.step.follow:advance',
        'seq.step.connect:already',
        'blocked.send:retry',
        'blocked.invite_cap:retry',
        'blocked.inmail_credits:retry',
        'seq.setup_unipile:resume',
        'seq.unresolved_vars:retry',
        'seq.fork:fanout',
        'seq.join:advance',
        'seq.spawn:detached',
        'seq.switch:route',
        'status.call_waiting:done',
        'status.reminder_waiting:done',
      ],
    },
    'seq.validate': {
      x: 702,
      y: 909,
      headerH: 26,
      outOrder: ['seq.validate:ok', 'seq.validate:unresolved', 'seq.validate:missing_content'],
    },
    'seq.hours_gate': { x: 997, y: 931, headerH: 26, outOrder: ['seq.hours_gate:open', 'seq.hours_gate:closed'] },
    'seq.step.call': { x: 854, y: 1047, headerH: 26, outOrder: ['seq.step.call:waiting', 'seq.step.call:failed'] },
    'seq.step.reminder': {
      x: 1131,
      y: 1047,
      headerH: 26,
      outOrder: ['seq.step.reminder:waiting', 'seq.step.reminder:failed'],
    },
    'seq.step.message': {
      x: 853,
      y: 1152,
      headerH: 26,
      outOrder: [
        'seq.step.message:advance',
        'seq.step.message:reply',
        'seq.step.message:optout',
        'seq.step.message:interrupt',
        'seq.step.message:blocked',
        'seq.step.message:account_down',
        'seq.step.message:unresolved',
      ],
    },
    'seq.step.connect': {
      x: 853,
      y: 1346,
      headerH: 26,
      outOrder: [
        'seq.step.connect:sent',
        'seq.step.connect:already',
        'seq.step.connect:invite_cap',
        'seq.step.connect:blocked',
        'seq.step.connect:account_down',
        'seq.step.connect:unresolved',
      ],
    },
    'seq.await_connect': {
      x: 272,
      y: 1415,
      headerH: 26,
      outOrder: ['seq.await_connect:accepted', 'seq.await_connect:no_connect', 'seq.await_connect:replied'],
    },
    'seq.step.inmail': {
      x: 845,
      y: 1524,
      headerH: 26,
      outOrder: [
        'seq.step.inmail:advance',
        'seq.step.inmail:interrupt',
        'seq.step.inmail:no_credits',
        'seq.step.inmail:blocked',
        'seq.step.inmail:account_down',
        'seq.step.inmail:unresolved',
      ],
    },
    'seq.step.email': {
      x: 852,
      y: 1703,
      headerH: 26,
      outOrder: ['seq.step.email:advance', 'seq.step.email:failed', 'seq.step.email:unresolved'],
    },
    'seq.wait': { x: -47, y: 1039, headerH: 26 },
    'seq.step.like': {
      x: 851,
      y: 1836,
      headerH: 26,
      outOrder: [
        'seq.step.like:advance',
        'seq.step.like:retry',
        'seq.step.like:account_down',
        'seq.step.like:interrupt',
      ],
    },
    'seq.step.follow': {
      x: 852,
      y: 1985,
      headerH: 26,
      outOrder: [
        'seq.step.follow:advance',
        'seq.step.follow:retry',
        'seq.step.follow:account_down',
        'seq.step.follow:interrupt',
      ],
    },
    'terminal.finished': { x: 334, y: 1307, headerH: 26 },
    'seq.waiting_hours': { x: 1504, y: 947, headerH: 26 },
    'blocked.send': {
      x: 1349,
      y: 1182,
      headerH: 100,
      inOrder: [
        'seq.step.connect:blocked',
        'seq.step.message:blocked',
        'seq.step.inmail:blocked',
        'seq.step.like:retry',
        'seq.step.follow:retry',
      ],
    },
    'seq.setup_unipile': {
      x: 1369,
      y: 1445,
      headerH: 100,
      inOrder: [
        'seq.step.connect:account_down',
        'seq.step.message:account_down',
        'seq.step.inmail:account_down',
        'seq.step.like:account_down',
        'seq.step.follow:account_down',
      ],
    },
    'terminal.errored': {
      x: 1621,
      y: 1847,
      headerH: 154,
      inOrder: [
        'seq.draft:failed',
        'seq.step.message:interrupt',
        'seq.step.inmail:interrupt',
        'seq.step.like:interrupt',
        'seq.step.follow:interrupt',
        'seq.step.call:failed',
        'seq.step.reminder:failed',
        'seq.step.email:failed',
      ],
    },
    'blocked.invite_cap': { x: 1320, y: 1354, headerH: 26 },
    'blocked.inmail_credits': { x: 1615, y: 1580, headerH: 26 },
    'terminal.no_connect': { x: 267, y: 1536, headerH: 26 },
    'terminal.responded': {
      x: 286,
      y: 1721,
      headerH: 26,
      inOrder: ['seq.reply_watch:responded', 'seq.await_connect:replied', 'seq.step.message:reply'],
    },
    'terminal.suppressed': { x: 594, y: 1287, headerH: 26 },
    'seq.unresolved_vars': {
      x: 1340,
      y: 1618,
      headerH: 82,
      inOrder: [
        'seq.validate:unresolved',
        'seq.step.message:unresolved',
        'seq.step.connect:unresolved',
        'seq.step.inmail:unresolved',
        'seq.step.email:unresolved',
      ],
    },
    'status.call_waiting': { x: 1454, y: 851, headerH: 26 },
    'status.reminder_waiting': { x: 1682, y: 1220, headerH: 26 },
    'status.missing_content': { x: 1130, y: 1740, headerH: 26 },
    'seq.reply_watch': { x: 246, y: 1590, headerH: 26 },
    'seq.fork': { x: -61, y: 1138, headerH: 26 },
    'seq.join': { x: -33, y: 965, headerH: 26 },
    'seq.spawn': { x: -75, y: 1219, headerH: 26 },
    'seq.switch': { x: -89, y: 1300, headerH: 26 },
    'status.disqualified': { x: -692, y: 905, headerH: 26, inOrder: ['qualify.rules:fail', 'qualify.icp:no'] },
  },
  edges: {
    'loader.cron->loader.pull': { exit: 'right', entry: 'left' },
    'loader.pull->loader.stage': { exit: 'right', entry: 'right' },
    'loader.stage->recontact.gate': { exit: 'left', entry: 'right' },
    'recontact.gate:pass': { exit: 'left', entry: 'left' },
    'recontact.gate:engaged': { exit: 'left', entry: 'right' },
    'recontact.gate:contacted': { exit: 'left', entry: 'right' },
    'title.screen:pass': { exit: 'right', entry: 'top' },
    'title.screen:reject': { exit: 'left', entry: 'top' },
    'seq.add->seq.dupe_check': { exit: 'right', entry: 'top' },
    'seq.dupe_check:clear': { exit: 'left', entry: 'top' },
    'seq.dupe_check:duplicate': { exit: 'left', entry: 'right' },
    'status.adding->research.should': { exit: 'left', entry: 'top' },
    'research.should:never': { exit: 'left', entry: 'left' },
    'research.should:stale': { exit: 'left', entry: 'left' },
    'research.should:fresh': { exit: 'left', entry: 'top' },
    'unipile.enabled:success': { exit: 'right', entry: 'left' },
    'unipile.enabled:disconnected': { exit: 'left', entry: 'top' },
    'unipile.slot->status.researching': { exit: 'right', entry: 'top' },
    'unipile.slot->status.waiting_slot': { exit: 'right', entry: 'left' },
    'cron.sweep->unipile.slot': { exit: 'left', entry: 'top' },
    'cron.sweep_ratelimit_unipile->unipile.slot': { exit: 'right', entry: 'top' },
    'status.researching->unipile.fetch': { exit: 'left', entry: 'top' },
    'unipile.fetch->unipile.slot.release': { exit: 'right', entry: 'top' },
    'unipile.fetch:success': { exit: 'left', entry: 'right', waypoints: [{ x: 329, y: 494 }] },
    'title.rescreen:pass': { exit: 'left', entry: 'top' },
    'title.rescreen:reject': { exit: 'left', entry: 'left' },
    'tavily.slot->status.gathering_sources': { exit: 'left', entry: 'right' },
    'status.gathering_sources->tavily.search': { exit: 'left', entry: 'top' },
    'tavily.slot->status.waiting_tavily': { exit: 'right', entry: 'right' },
    'cron.sweep_tavily->tavily.slot': { exit: 'right', entry: 'top' },
    'tavily.search->tavily.slot.release': { exit: 'right', entry: 'top' },
    'cron.sweep_ratelimit_tavily->tavily.slot': { exit: 'right', entry: 'top' },
    'unipile.fetch:unavailable': { exit: 'right', entry: 'left' },
    'contact.delete:next': { exit: 'right', entry: 'left' },
    'contact.purge:deleted': { exit: 'right', entry: 'top' },
    'contact.purge:skipped': { exit: 'left', entry: 'top' },
    'contact.purge:failed': { exit: 'left', entry: 'right' },
    'unipile.fetch:review': { exit: 'left', entry: 'top' },
    'status.pending_review->human.review_identity': { exit: 'right', entry: 'left' },
    'unipile.fetch:disconnected': { exit: 'left', entry: 'right' },
    'unipile.fetch:ratelimited': { exit: 'right', entry: 'left' },
    'unipile.fetch:no_salesnav': { exit: 'left', entry: 'top' },
    'unipile.fetch:misconfigured': { exit: 'right', entry: 'left' },
    'unipile.fetch:failed': { exit: 'right', entry: 'left' },
    'unipile.disconnected->action.reconnect_linkedin': { exit: 'left', entry: 'left', waypoints: [{ x: 387, y: 265 }] },
    'status.needs_salesnav->action.add_salesnav': { exit: 'left', entry: 'left' },
    'tavily.search:success': { exit: 'left', entry: 'right' },
    'tavily.search:no_results': { exit: 'left', entry: 'right' },
    'tavily.search:ratelimited': { exit: 'right', entry: 'left' },
    'tavily.search:failed': { exit: 'left', entry: 'right' },
    'web_search_failed:continue': { exit: 'left', entry: 'top' },
    'dossier.synthesize:success': { exit: 'right', entry: 'top' },
    'dossier.synthesize:failed': { exit: 'left', entry: 'left' },
    'qualify.rules:pass': { exit: 'left', entry: 'top' },
    'qualify.rules:fail': { exit: 'left', entry: 'top' },
    'qualify.icp:yes': { exit: 'right', entry: 'left' },
    'qualify.icp:no': { exit: 'left', entry: 'right' },
    'status.enrolled->seq.run_step': { exit: 'right', entry: 'top' },
    'seq.draft:ready': { exit: 'left', entry: 'top' },
    'seq.draft:failed': { exit: 'right', entry: 'left' },
    'human.review_drafts:approve': { exit: 'right', entry: 'left' },
    'human.review_drafts:reject': { exit: 'left', entry: 'left' },
    'seq.enroll:bound': { exit: 'right', entry: 'top' },
    'seq.run_step:call': { exit: 'right', entry: 'left' },
    'seq.run_step:reminder': { exit: 'right', entry: 'left' },
    'seq.run_step:message': { exit: 'right', entry: 'left' },
    'seq.run_step:connect': { exit: 'right', entry: 'left' },
    'seq.run_step:inmail': { exit: 'right', entry: 'left' },
    'seq.run_step:email': { exit: 'right', entry: 'left' },
    'seq.run_step:wait': { exit: 'left', entry: 'right' },
    'seq.run_step:like': { exit: 'right', entry: 'left' },
    'seq.run_step:follow': { exit: 'right', entry: 'left' },
    'seq.run_step:done': { exit: 'left', entry: 'left' },
    'seq.run_step:send_window': { exit: 'right', entry: 'left' },
    'seq.validate:ok': { exit: 'right', entry: 'left' },
    'seq.validate:unresolved': { exit: 'right', entry: 'top' },
    'seq.validate:missing_content': { exit: 'right', entry: 'left' },
    'seq.hours_gate:open': { exit: 'right', entry: 'top' },
    'seq.hours_gate:closed': { exit: 'right', entry: 'left' },
    'seq.step.call:waiting': { exit: 'right', entry: 'left' },
    'seq.step.reminder:waiting': { exit: 'right', entry: 'left' },
    'status.call_waiting:done': { exit: 'left', entry: 'right' },
    'status.reminder_waiting:done': { exit: 'left', entry: 'right' },
    'status.missing_content:retry': { exit: 'left', entry: 'top' },
    'seq.step.message:advance': { exit: 'right', entry: 'top' },
    'seq.step.connect:sent': { exit: 'left', entry: 'top' },
    'seq.await_connect:accepted': { exit: 'left', entry: 'top' },
    'seq.await_connect:no_connect': { exit: 'right', entry: 'right' },
    'seq.await_connect:replied': { exit: 'left', entry: 'left' },
    'seq.step.inmail:advance': { exit: 'right', entry: 'top' },
    'seq.step.email:advance': { exit: 'right', entry: 'top' },
    'seq.wait:advance': { exit: 'left', entry: 'top' },
    'seq.step.like:advance': { exit: 'right', entry: 'top' },
    'seq.step.follow:advance': { exit: 'right', entry: 'top' },
    'seq.step.connect:already': { exit: 'right', entry: 'top' },
    'seq.step.connect:invite_cap': { exit: 'right', entry: 'left' },
    'seq.step.connect:blocked': { exit: 'right', entry: 'left' },
    'seq.step.connect:account_down': { exit: 'right', entry: 'left' },
    'seq.step.message:reply': { exit: 'left', entry: 'right' },
    'seq.step.message:optout': { exit: 'left', entry: 'top' },
    'seq.step.message:interrupt': { exit: 'right', entry: 'left' },
    'seq.step.message:blocked': { exit: 'right', entry: 'left' },
    'seq.step.message:account_down': { exit: 'right', entry: 'left' },
    'seq.step.inmail:interrupt': { exit: 'right', entry: 'left' },
    'seq.step.inmail:no_credits': { exit: 'right', entry: 'left' },
    'seq.step.inmail:blocked': { exit: 'right', entry: 'left' },
    'seq.step.inmail:account_down': { exit: 'right', entry: 'left' },
    'seq.step.like:retry': { exit: 'right', entry: 'left' },
    'seq.step.like:account_down': { exit: 'right', entry: 'left' },
    'seq.step.like:interrupt': { exit: 'right', entry: 'left' },
    'seq.step.follow:retry': { exit: 'right', entry: 'left' },
    'seq.step.follow:account_down': { exit: 'right', entry: 'left' },
    'seq.step.follow:interrupt': { exit: 'right', entry: 'left' },
    'seq.step.call:failed': { exit: 'right', entry: 'left' },
    'seq.step.reminder:failed': { exit: 'right', entry: 'left' },
    'seq.step.email:failed': { exit: 'right', entry: 'left' },
    'blocked.send:retry': { exit: 'right', entry: 'top' },
    'blocked.invite_cap:retry': { exit: 'right', entry: 'top' },
    'blocked.inmail_credits:retry': { exit: 'right', entry: 'top' },
    'seq.setup_unipile:resume': { exit: 'right', entry: 'top' },
    'seq.step.message:unresolved': { exit: 'right', entry: 'left' },
    'seq.step.connect:unresolved': { exit: 'right', entry: 'left' },
    'seq.step.inmail:unresolved': { exit: 'right', entry: 'left' },
    'seq.step.email:unresolved': { exit: 'right', entry: 'left' },
    'seq.unresolved_vars:retry': { exit: 'right', entry: 'top' },
    'seq.run_step:reply_watch': { exit: 'right', entry: 'right' },
    'seq.reply_watch:responded': { exit: 'right', entry: 'top' },
    'seq.run_step:group': { exit: 'left', entry: 'right' },
    'seq.fork:fanout': { exit: 'left', entry: 'top' },
    'seq.run_step:join': { exit: 'left', entry: 'right' },
    'seq.join:advance': { exit: 'left', entry: 'top' },
    'seq.run_step:spawn': { exit: 'left', entry: 'right' },
    'seq.spawn:detached': { exit: 'left', entry: 'top' },
    'seq.run_step:switch': { exit: 'left', entry: 'right' },
    'seq.switch:route': { exit: 'left', entry: 'top' },
  },
};
